<script lang="ts">
  import Select from "svelte-select"
  import { fetchProperties } from "../../../../../../../api/kehrblatt/property-registry/properties"
  import { formatPropertyUnitSelectLabel } from "../../../../../../../_shared/_logic/unit-labels"

  type PropertyUnitSelectItem = {
    value: string
    label: string
    property: Property
    unitId: string
  }

  type Props = {
    organizationId: string
    seedProperties?: Property[]
    excludeValues?: string[]
    disabled?: boolean
    brightBackground?: boolean
    id?: string
    placeholder?: string
    onChange?: (values: string[]) => void
  }

  type PublicProps = Props & {
    value?: string[]
  }

  const minimumSearchLength = 2
  const searchLimit = 25

  let {
    organizationId,
    seedProperties = [],
    excludeValues = [],
    disabled = false,
    brightBackground = true,
    id = "property-unit-async-multiselect",
    placeholder = "Liegenschaften auswählen",
    onChange = undefined,
    value = $bindable<string[]>([]),
  }: PublicProps = $props()

  let items = $state<PropertyUnitSelectItem[]>([])
  let selectedItems = $state<PropertyUnitSelectItem[]>([])
  let filterText = $state("")
  let loading = $state(false)
  let syncingSelectedItems = false
  let lastAppliedValueKey = $state<string | null>(null)
  let lastAppliedSelectionKey = $state<string | null>(null)
  let loadedProperties = $state<Property[]>([])

  const pendingPropertyIds = new Set<string>()

  const normalizeId = (input: string | null | undefined) => `${input ?? ""}`.trim()
  const toCombinedValue = (propertyId: string, unitId: string | null | undefined) =>
    `${normalizeId(propertyId)}::${normalizeId(unitId)}`

  const mapPropertyToItems = (property: Property): PropertyUnitSelectItem[] =>
    (property.units ?? [])
      .map((unit) => {
        const unitId = normalizeId(unit.id)

        if (!unitId) return null

        return {
          value: toCombinedValue(property.id, unitId),
          label: formatPropertyUnitSelectLabel(property, unit),
          property,
          unitId,
        }
      })
      .filter((entry): entry is PropertyUnitSelectItem => Boolean(entry))

  const mergeCachedProperties = (current: Property[], incoming: Property[]) => {
    if (!incoming.length) return current

    const byId = new Map(current.map((entry) => [normalizeId(entry.id), entry]))
    let changed = false
    for (const property of incoming) {
      const propertyId = normalizeId(property.id)

      if (!propertyId) continue

      if (byId.get(propertyId) !== property) {
        byId.set(propertyId, property)
        changed = true
      }
    }

    return changed ? Array.from(byId.values()) : current
  }

  const cachedProperties = $derived.by(() =>
    mergeCachedProperties(mergeCachedProperties([], seedProperties), loadedProperties),
  )
  const selectedValueSet = $derived(
    new Set((value ?? []).map((entry) => normalizeId(entry)).filter(Boolean)),
  )
  const selectedValuesKey = $derived(
    Array.from(new Set((value ?? []).map((entry) => normalizeId(entry)).filter(Boolean))).join("|"),
  )
  const excludedValueSet = $derived(
    new Set(
      (excludeValues ?? [])
        .map((entry) => normalizeId(entry))
        .filter((entry) => Boolean(entry) && !selectedValueSet.has(entry)),
    ),
  )
  const selectedPropertyIds = $derived(
    Array.from(
      new Set(
        (value ?? []).map((entry) => normalizeId(entry).split("::")[0] ?? "").filter(Boolean),
      ),
    ),
  )
  const currentSelectedItems = $derived(
    cachedProperties
      .flatMap(mapPropertyToItems)
      .filter((entry) => selectedValueSet.has(entry.value)),
  )

  const emptyMessage = $derived.by(() => {
    if (loading) return "Suche läuft…"

    if (filterText.trim().length < minimumSearchLength) return "Mindestens 2 Zeichen für Ergebnisse"

    return "Keine Ergebnisse"
  })

  const ensureSelectedPropertiesLoaded = async (propertyIds: string[]) => {
    const missingIds = propertyIds.filter((propertyId) => {
      if (!propertyId) return false

      if (pendingPropertyIds.has(propertyId)) return false

      return !cachedProperties.some((entry) => normalizeId(entry.id) === propertyId)
    })

    if (!organizationId || !missingIds.length) return

    missingIds.forEach((propertyId) => pendingPropertyIds.add(propertyId))
    try {
      const loaded = await fetchProperties({
        organizationId,
        propertyIds: missingIds,
        limit: missingIds.length,
      })
      loadedProperties = mergeCachedProperties(loadedProperties, loaded)
    } catch (err) {
      console.error("property_unit_async_multiselect_load_failed", err)
    } finally {
      missingIds.forEach((propertyId) => pendingPropertyIds.delete(propertyId))
    }
  }

  const loadOptions = async (input: string) => {
    const trimmed = input.trim()

    if (trimmed.length < minimumSearchLength) {
      items = []

      return []
    }

    loading = true
    try {
      const results = await fetchProperties({
        organizationId,
        search: trimmed,
        limit: searchLimit,
      })
      loadedProperties = mergeCachedProperties(loadedProperties, results)
      const nextItems = results
        .flatMap(mapPropertyToItems)
        .filter((entry) => !excludedValueSet.has(entry.value))
      items = nextItems

      return nextItems
    } catch (err) {
      console.error("property_unit_async_multiselect_search_failed", err)
      items = []

      return []
    } finally {
      loading = false
    }
  }

  $effect(() => {
    if (selectedValuesKey === lastAppliedValueKey) return

    if (!selectedPropertyIds.length) {
      if (selectedItems.length) {
        syncingSelectedItems = true
        selectedItems = []
        lastAppliedSelectionKey = ""
        queueMicrotask(() => {
          syncingSelectedItems = false
        })
      }

      lastAppliedValueKey = selectedValuesKey
      lastAppliedSelectionKey = ""

      return
    }

    if (currentSelectedItems.length === value.length) {
      const currentKey = selectedItems.map((item) => item.value).join("|")
      const nextKey = currentSelectedItems.map((item) => item.value).join("|")

      if (currentKey !== nextKey) {
        syncingSelectedItems = true
        selectedItems = currentSelectedItems
        lastAppliedSelectionKey = nextKey
        queueMicrotask(() => {
          syncingSelectedItems = false
        })
      }

      lastAppliedValueKey = selectedValuesKey
      lastAppliedSelectionKey = nextKey

      return
    }

    void ensureSelectedPropertiesLoaded(selectedPropertyIds)
  })

  const applySelection = (nextItems: PropertyUnitSelectItem[]) => {
    if (syncingSelectedItems) return

    const nextValues = Array.from(
      new Set(nextItems.map((item) => normalizeId(item.value)).filter(Boolean)),
    )
    const nextKey = nextValues.join("|")

    if (nextKey === lastAppliedSelectionKey) return

    value = nextValues
    lastAppliedValueKey = nextKey
    lastAppliedSelectionKey = nextKey
    onChange?.(nextValues)
  }

  const handleSelectChange = (
    event: CustomEvent<PropertyUnitSelectItem[] | PropertyUnitSelectItem | null>,
  ) => {
    filterText = ""
    const detail = event.detail
    applySelection(Array.isArray(detail) ? detail : detail ? [detail] : selectedItems)
  }

  const handleSelectClear = () => {
    filterText = ""
    applySelection([])
  }
</script>

<label class="fieldShell" data-input-id={id} class:disabled class:bright={brightBackground}>
  <span class="labelspan hasValue">{placeholder}</span>
  <div class="select-input-shell">
    <Select
      bind:value={selectedItems}
      bind:filterText
      multiple={true}
      {disabled}
      {loading}
      {loadOptions}
      {items}
      {id}
      placeholder=" "
      clearable={true}
      debounceWait={300}
      class={brightBackground ? "svelte-select--bright" : "svelte-select--dark"}
      itemId="value"
      label="label"
      showChevron={true}
      closeListOnChange={false}
      inputAttributes={{ autocomplete: "off" }}
      on:change={handleSelectChange}
      on:clear={handleSelectClear}
    >
      <svelte:fragment slot="selection" let:selection>
        <div class="select-selection">
          <span class="select-selection__label"
            >{selection?.label ?? selection?.name ?? selection?.value ?? ""}</span
          >
        </div>
      </svelte:fragment>

      <svelte:fragment slot="item" let:item>
        <div class="select-item">
          <span class="select-item__label">{item.label ?? item.name ?? item.value}</span>
        </div>
      </svelte:fragment>

      <svelte:fragment slot="empty">
        <div class="emptyState">{emptyMessage}</div>
      </svelte:fragment>
    </Select>
  </div>
</label>

<style lang="less">
  .fieldShell {
    width: 100%;
    max-width: 100%;
  }

  :global(label.fieldShell.error .svelte-select) {
    border-color: var(--warning-100);
  }

  .fieldShell :global(.svelte-select),
  .fieldShell :global(.select-input-shell) {
    width: 100%;
  }

  .emptyState {
    padding: 0.6rem 0.85rem;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    font-size: 0.92rem;
  }

  .fieldShell.bright .emptyState {
    color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
  }
</style>
